import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { ArrowLeft, CheckCircle2, Users, TrendingUp, Clock, Target, Award, BarChart3, Loader2 } from 'lucide-react';
import PreSignDialog from '../PreSign/PreSignDialog';
import ContactFormModal from '../ContactForm/ContactFormModal';

const SmartAssessments = () => {
    const navigate = useNavigate();
    const [openContactForm, setOpenContactForm] = useState(false);
    const [academyPlans, setAcademyPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        try {
            ReactGA.send({ hitType: 'pageview', page: '/products/smart-assessments', title: 'Smart Assessments' });
        } catch { }
    }, []);

    const workflow = [
        {
            step: '01',
            title: 'Capture',
            description: 'Training sessions are recorded using any standard smartphone and uploaded to the platform.',
            icon: '📱'
        },
        {
            step: '02',
            title: 'AI Analysis',
            description: 'The platform\'s Visual Intelligence Platform™ utilizes specialized AI agents to autonomously observe and analyze the footage.',
            icon: '🤖'
        },
        {
            step: '03',
            title: 'Human-in-the-Loop Validation',
            description: 'The system combines automated AI analysis with expert coach review, ensuring that the final insights are both data-driven and technically sound.',
            icon: '✓'
        },
        {
            step: '04',
            title: 'Professional Reporting',
            description: 'The process culminates in a professional-grade visual report that documents student progress and offers recommendations for corrective actions.',
            icon: '📊'
        }
    ];

    const modules = [
        {
            category: 'Technical Skills',
            skills: ['Forehand and Backhand Grip', 'Airtoss', 'Backhand Serve', 'Net Play (Keeps & Lifts)'],
            color: 'from-emerald to-emerald/80',
            icon: Target
        },
        {
            category: 'Physicality & Agility',
            skills: ['Box Running', 'Stationary Jumping', 'Balance', 'Throwing/Catching'],
            color: 'from-blue-500 to-blue-600',
            icon: TrendingUp
        },
        {
            category: 'Fitness Metrics',
            skills: ['Crunches', 'Flamingo Balance Tests', 'HandTaps', 'Pushups', 'Toe Touches'],
            color: 'from-purple-500 to-purple-600',
            icon: BarChart3
        }
    ];

    const benefits = {
        academies: [
            { metric: '70%', label: 'Reduction in Assessment Time' },
            { metric: '50%', label: 'Increase in Coach Productivity' },
            { metric: '5+ hrs', label: 'Saved Per Week Per Coach' }
        ],
        players: [
            { label: 'Transparent Performance Metrics' },
            { label: 'Visual Proof of Improvement' },
            { label: 'Winning Portfolio of Progress' },
            { label: 'Enhanced Parent Engagement' }
        ]
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-deep-charcoal">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 py-20 relative z-10">
                    <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => navigate('/products')}
                        className="flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-semibold">Back to Products</span>
                    </motion.button>

                    <div className="max-w-5xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight uppercase text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">
                                Smart Assessments
                            </h1>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-4xl mx-auto mb-8"
                        >
                            Automate and standardize athlete skill evaluation by transforming smartphone videos into structured performance data
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="text-lg text-gray-400 max-w-3xl mx-auto mb-12"
                        >
                            Deliver elite-level feedback without expensive infrastructure
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                        >
                            <button
                                onClick={() => setOpenContactForm(true)}
                                className="px-12 py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-lg rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
                            >
                                Talk to Us Today
                            </button>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Core Functionality Section */}
            <section className="relative py-24 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-emerald/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-sm md:text-base font-bold uppercase tracking-widest mb-3" style={{ color: '#247E45' }}>
                            Seamless Workflow
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
                            Capture-to-Report <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Process</span>
                        </h2>
                        <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
                            A four-step process designed for ease of use in daily training environments
                        </p>
                    </motion.div>

                    {/* Workflow Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Workflow.png"
                            alt="Smart Assessments Workflow - Capture to Report Process"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-200"
                        />
                    </motion.div>

                    {/* Workflow Steps */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
                        {workflow.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="relative bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-emerald hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                            >
                                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-lg" style={{ backgroundColor: '#247E45' }}>
                                    {item.step}
                                </div>
                                <div className="text-4xl mb-4 mt-4">{item.icon}</div>
                                <h3 className="text-xl font-black text-gray-900 mb-3">{item.title}</h3>
                                <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Assessment Modules Section */}
            <section className="relative py-24 bg-deep-charcoal overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">
                            Comprehensive Evaluation
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase mb-4">
                            Assessment <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Modules</span>
                        </h2>
                        <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto">
                            Modular evaluation criteria categorized by skill level
                        </p>
                    </motion.div>

                    {/* Modules Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Modules.png"
                            alt="Smart Assessments Modules - Technical Skills, Physicality, and Fitness"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-700"
                        />
                    </motion.div>

                    {/* Module Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
                        {modules.map((module, index) => {
                            const Icon = module.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-8 hover:border-emerald hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                                >
                                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-br ${module.color}`}>
                                        <Icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-black text-white mb-4">{module.category}</h3>
                                    <ul className="space-y-3">
                                        {module.skills.map((skill, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-gray-300">
                                                <CheckCircle2 className="w-5 h-5 text-emerald flex-shrink-0 mt-0.5" />
                                                <span className="text-sm">{skill}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Skill Level Badges */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="flex flex-wrap justify-center gap-4"
                    >
                        {['Beginner', 'Intermediate', 'Advanced'].map((level, index) => (
                            <div
                                key={index}
                                className="px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full"
                            >
                                <span className="text-white font-bold text-sm">{level}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Strategic Impact Section */}
            <section className="relative py-24 bg-white overflow-hidden">
                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-sm md:text-base font-bold uppercase tracking-widest mb-3" style={{ color: '#247E45' }}>
                            Measurable Results
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
                            Strategic <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Impact</span>
                        </h2>
                        <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
                            Solving the "Manual Assessment" bottleneck with AI-powered automation
                        </p>
                    </motion.div>

                    {/* Impact Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16 max-w-6xl mx-auto"
                    >
                        <img
                            src="/assets/Impacts.png"
                            alt="Smart Assessments Strategic Impact for Academies and Athletes"
                            className="w-full h-auto rounded-2xl shadow-2xl border-2 border-gray-200"
                        />
                    </motion.div>

                    {/* Benefits Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
                        {/* Academies & Coaches */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#247E45' }}>
                                    <Users className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900">For Academies & Coaches</h3>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mb-6">
                                {benefits.academies.map((item, index) => (
                                    <div key={index} className="text-center p-4 bg-white rounded-xl border border-gray-200">
                                        <div className="text-3xl font-black mb-2" style={{ color: '#247E45' }}>{item.metric}</div>
                                        <div className="text-xs text-gray-600 font-semibold">{item.label}</div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-gray-600 leading-relaxed">
                                Focus on actual training rather than administrative tasks. Reduce assessment fatigue and deliver consistent, objective feedback.
                            </p>
                        </motion.div>

                        {/* Players & Parents */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#247E45' }}>
                                    <Award className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900">For Players & Parents</h3>
                            </div>
                            <ul className="space-y-4 mb-6">
                                {benefits.players.map((item, index) => (
                                    <li key={index} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-emerald flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-700 font-semibold">{item.label}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-gray-600 leading-relaxed">
                                Solve the "Parent Disconnect" with visual proof of improvement. Build a winning portfolio that enhances engagement and trust.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-20 bg-deep-charcoal overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                            Ready to Transform Your Assessments?
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                            Join leading academies using Smart Assessments to deliver elite-level feedback
                        </p>
                        <button
                            onClick={() => setOpenContactForm(true)}
                            className="px-12 py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-lg rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
                        >
                            Talk to Us Today
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Contact Form Modal */}
            <ContactFormModal
                isOpen={openContactForm}
                onClose={() => setOpenContactForm(false)}
            />
        </div>
    );
};

export default SmartAssessments;